import React, { useCallback } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import {
  Box,
  Text,
  FontWeight,
  Display,
  Space,
  JustifyContent,
  Bold,
  Color,
  Icon,
  IconVariant,
  AlignItems,
  withCitrusTheme,
  Link,
} from '@gousto-internal/citrus-react'
import Immutable from 'immutable'
import snowplowTracker from 'middlewares/tracking/snowplow'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { browserHistory } from 'react-router'

import routes from 'config/routes'

import OrderRecipeBox from './OrderRecipeBox'

const OrderRecipes = ({
  recipes,
  orderId,
  periodId,
  portionsCount,
  deliveryDate,
  orderState,
  whenCutoff,
  maxNumRecipes,
}) => {
  const onCTAClick = useCallback(() => {
    datadogRum.addAction('choose_recipes_my_deliveries_button_clicked', { orderId })
    snowplowTracker(
      {
        actionType: 'choose_recipes_click',
        order_id: orderId,
        period_id: periodId,
        portion_size: portionsCount,
        delivery_date: deliveryDate,
        order_state: orderState,
      },
      {
        pathname: window.location.pathname,
      },
    )
    browserHistory.push(`${routes.client.menu}/${orderId}`)
  }, [deliveryDate, orderId, orderState, periodId, portionsCount])

  const LinkWithoutUnderline = withCitrusTheme(Link, () => ({
    textDecoration: 'none',
    boxShadow: 'none',
  }))

  return (
    <div>
      <Box display={Display.Flex} justifyContent={JustifyContent.SpaceBetween}>
        <Text fontWeight={FontWeight.Bold}>Chosen recipes</Text>
        {['menu open', 'recipes chosen'].indexOf(orderState) > -1 ? (
          <Box display={Display.Flex} alignItems={AlignItems.Center}>
            <LinkWithoutUnderline onClick={onCTAClick}>
              <Box
                display={Display.Flex}
                alignItems={AlignItems.Center}
                style={{ whiteSpace: 'nowrap' }}
              >
                <Text size={1} color={Color.Secondary_400}>
                  {orderState === 'recipes chosen' ? 'Edit recipes' : 'Choose recipes'}
                </Text>
                <Space size={1} direction="horizontal" />
                <Icon size={5} name="edit" variant={IconVariant.Actionable} />
              </Box>
            </LinkWithoutUnderline>
          </Box>
        ) : null}
      </Box>
      <Space size={4} direction="vertical" />
      <OrderRecipeBox recipes={recipes} orderState={orderState} maxNumRecipes={maxNumRecipes} />
      {orderState === 'menu open' ? (
        <Box paddingV={5}>
          <Text fontWeight={FontWeight.Bold}>You haven&apos;t chosen any recipes yet.</Text>
          <Space size={3} direction="vertical" />
          <Text>
            If you do not choose by <Bold>{whenCutoff}</Bold>, Gousto will send you a selection of
            recipes based on your subscription settings.
          </Text>
        </Box>
      ) : null}
    </div>
  )
}

OrderRecipes.propTypes = {
  recipes: ImmutablePropTypes.listOf(
    ImmutablePropTypes.contains({
      recipeImage: PropTypes.string,
      recipeTitle: PropTypes.string,
    }),
  ),
  orderId: PropTypes.string,
  periodId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  portionsCount: PropTypes.string,
  deliveryDate: PropTypes.string,
  orderState: PropTypes.string,
  whenCutoff: PropTypes.string,
  maxNumRecipes: PropTypes.number,
}

OrderRecipes.defaultProps = {
  recipes: Immutable.List([]),
  orderId: '',
  periodId: '',
  portionsCount: '',
  deliveryDate: '',
  orderState: '',
  whenCutoff: '',
  maxNumRecipes: 4,
}

export default OrderRecipes
